import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Heart, Mail, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useLanguage, useTranslation } from "../contexts/LanguageContext";
import { ModerationWarningModal, BannedModal } from "../components/ModerationModals";

import { API } from "../config";

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [moderationWarning, setModerationWarning] = useState(null);
  const [bannedMessage, setBannedMessage] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(false);

// Auto-moderation system v1.0
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      // Handle banned user (403) before parsing JSON
      if (response.status === 403) {
        const data = await response.json();
        if (data.detail?.startsWith("BANNED:")) {
          setBannedMessage(data.detail.replace("BANNED:", ""));
          return;
        }
        alert(data.detail || t('auth.loginError'));
        return;
      }

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("session_token", data.session_token);
        // For existing users, mark onboarding as completed (they don't need to see it again)
        localStorage.setItem("loveconnect_onboarding_completed", "true");
        
        // Check for moderation warning
        if (data.user.moderation_warning?.show_warning) {
          setModerationWarning(data.user.moderation_warning);
          setPendingNavigation(true);
        } else {
          navigate("/discover");
        }
      } else {
        alert(data.detail || t('auth.loginError'));
      }
    } catch (error) {
      console.error("Login error:", error);
      alert(t('auth.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleWarningClose = () => {
    setModerationWarning(null);
    if (pendingNavigation) {
      navigate("/discover");
    }
  };

  const handleBannedClose = () => {
    setBannedMessage(null);
    // Stay on login page
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-6" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
        <Button 
          variant="ghost" 
          className="text-muted-foreground hover:text-white"
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('common.back')}
        </Button>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/logo-original.png" alt="Love Connect +" className="w-16 h-16" />
              <span className="font-heading text-3xl font-bold text-white">Love Connect +</span>
            </div>
            <p className="text-muted-foreground">{t('auth.connectToContinu')}</p>
          </div>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full h-14 rounded-2xl border-white/20 hover:bg-white/5 mb-6"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.continueWithGoogle')}
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-4 text-muted-foreground">{t('auth.or')}</span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-14 rounded-2xl bg-muted/50 border-transparent focus:border-primary"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-14 rounded-2xl bg-muted/50 border-transparent focus:border-primary"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  aria-label={showPassword ? (language === 'fr' ? 'Masquer le mot de passe' : 'Hide password') : (language === 'fr' ? 'Afficher le mot de passe' : 'Show password')}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-14 rounded-2xl bg-primary text-white font-bold text-lg hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('auth.login')
              )}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center mt-6 text-muted-foreground">
            {t('auth.noAccount')}{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Moderation Warning Modal */}
      {moderationWarning && (
        <ModerationWarningModal 
          warning={moderationWarning} 
          onClose={handleWarningClose} 
        />
      )}

      {/* Banned Modal */}
      {bannedMessage && (
        <BannedModal 
          message={bannedMessage} 
          onClose={handleBannedClose} 
        />
      )}
    </div>
  );
}
